// Client-side twin of the write/decision filtering in `data-sorting/backend/app/session/trail.py`
// (`_is_write` / `_decisions`), shared by anything that needs to read a session's step log in the browser
// without waiting for a matching server render — currently `WorkingDataHistoryPanel` (falls back to this
// when a deployed `data-sorting` service hasn't shipped `methodologySummaryHtml` yet) and
// `generateMethodologyDraft` (builds a prose draft client-side, no backend call).
//
// Extracted to one file rather than left duplicated a second time: the two callers must never disagree
// about which steps counted as a "decision", and a second copy is how that drifts.

// Python truthiness for `outputs`/`row_delta`: an empty {} or [] is falsy in Python but truthy in JS, so a
// plain `Boolean(...)` here would count a step with an empty (but present) `outputs` object as a write.
const pyTruthy = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
};

export const escapeHtml = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const isWriteStep = (step) =>
  Object.prototype.hasOwnProperty.call(step, "mutated")
    ? Boolean(step.mutated)
    : pyTruthy(step.outputs) || pyTruthy(step.row_delta);

// The write steps that represent a DECISION rather than moving bytes between frame names — mirrors
// `_decisions` in trail.py exactly (same criterion: did the model describe it with a `note`), so this can
// never show a different list than the one the backend would render from the same steps.
export const decisionsOf = (writes) => {
  const described = writes.filter((step) => String(step.note || "").trim());
  if (described.length) return described;
  const anyHasNoteKey = writes.some((step) => Object.prototype.hasOwnProperty.call(step, "note"));
  return anyHasNoteKey ? [] : writes;
};
